/**
 * The sheet plan the AutoCAD connector draws the 03 set from.
 *
 * Mirrors `SheetPlan` in services/recognition-worker/AEFloorPlan.Contracts/ConversionModels.cs;
 * the two move together. Every coordinate is in model inches with Y up, page-local: the
 * connector's own transform of a preview pixel (x * inchesPerPreviewPixel,
 * -y * inchesPerPreviewPixel) before it adds the page's stacking offset.
 */

export interface PlanPoint {
  readonly x: number;
  readonly y: number;
}

export interface PlannedSheet {
  /** 1-based position in the floor's sheet series: 1 is AV201 on the first floor. */
  readonly index: number;
  readonly areaLetter: string;
  /** 0-based, largest building first. */
  readonly building: number;
  /** Model-space centre and size of the visible area; the viewport is sized to it. */
  readonly viewCenter: PlanPoint;
  readonly viewWidth: number;
  readonly viewHeight: number;
  /** What the viewport shows: this sheet's area and the overlap past each cut. One viewport per polygon. */
  readonly clipPolygons: readonly (readonly PlanPoint[])[];
  /** This sheet's own area, undilated, for the keyplan hatch. */
  readonly hatchPolygons: readonly (readonly PlanPoint[])[];
  /** Where the area letter goes on the keyplan. */
  readonly labelPoint: PlanPoint;
}

export interface MatchlineLabel {
  /** The sheet index this label names. */
  readonly sheet: number;
  readonly position: PlanPoint;
  readonly rotationDegrees: 0 | 90;
  /** Which end of the text sits at `position`. */
  readonly horizontal: "left" | "right";
  /** "bottom" sits the text on the line's left-hand side as read; "top" hangs it below. */
  readonly vertical: "bottom" | "top";
}

/** A cut as one sheet draws it, on its own viewport edge. Its neighbour draws its own. */
export interface PlannedMatchline {
  /** The sheet that draws this line. */
  readonly sheet: number;
  readonly neighbour: number;
  readonly points: readonly PlanPoint[];
  readonly labels: readonly MatchlineLabel[];
}

export interface SheetPlan {
  readonly version: 1;
  /** The viewport scale the plan was fitted at, paper inches per model inch. */
  readonly scale: number;
  /** The viewport the plan was fitted to, paper inches. The connector refuses a plan for another size. */
  readonly viewportWidthPaper: number;
  readonly viewportHeightPaper: number;
  /** Matchline label text height in model inches at that scale. */
  readonly labelTextHeight: number;
  readonly sheets: readonly PlannedSheet[];
  readonly matchlines: readonly PlannedMatchline[];
  readonly warnings: readonly string[];
}

export interface PlannerLimits {
  /** Viewport size on paper, inches. */
  readonly viewportWidthPaper: number;
  readonly viewportHeightPaper: number;
  /** Paper inches per model inch. */
  readonly scale: number;
}

/** The 03 drawing's standard viewport: 27" x 24" at 1/8" = 1'-0". */
export const DEFAULT_LIMITS: PlannerLimits = {
  viewportWidthPaper: 27,
  viewportHeightPaper: 24,
  scale: 1 / 96,
};
